import { useEffect, useRef } from 'react';
import { useQueryState } from 'nuqs';
import { useCopilotStatus } from '@customerio/hydra-copilot/react';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { open, useSidebarActive } from '@/features/sidebar';
import { isEmptyString } from '@/utils/string';
import { useIsFullScreenAssistantPage } from './use-is-fullscreen-assistant-page';

/**
 * Manages the copilot's URL query parameters:
 *
 * - `ai-session` — Present in the URL while the copilot is visible (sidebar
 *   open or fullscreen page). Cleared when the copilot is hidden. On page
 *   load, restores the previous session and opens the copilot.
 * - `ai-message` — Prefills the chat input and opens the copilot. Consumed
 *   once on mount. Takes priority over `ai-session` when both are present.
 */
export function useCopilotSessionUrlSync(
  session: CopilotSession & { reset: () => CopilotSession },
  setInput: (value: string) => void,
) {
  const [sessionIdParam, setSessionIdParam] = useQueryState('ai-session');
  const [aiMessageParam, setAiMessageParam] = useQueryState('ai-message');
  const status = useCopilotStatus(session);
  const hasHandledMount = useRef(false);
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const isSidebarOpen = useSidebarActive('copilot');
  const isCopilotVisible = isFullScreenAssistantPage || isSidebarOpen;

  // On mount: consume one-shot params and/or restore a previous session.
  useEffect(() => {
    if (hasHandledMount.current) {
      return;
    }
    hasHandledMount.current = true;

    // ai-message takes priority — prefill input and start a fresh chat
    if (!isEmptyString(aiMessageParam)) {
      void setAiMessageParam(null);
      setInput(aiMessageParam);

      if (!isEmptyString(sessionIdParam)) {
        void setSessionIdParam(null);
      }

      if (!isFullScreenAssistantPage) {
        open('copilot');
      }
      return;
    }

    // Restore session from URL
    if (!isEmptyString(sessionIdParam)) {
      session
        .initialize(sessionIdParam)
        .then(() => {
          if (!isFullScreenAssistantPage) {
            open('copilot');
          }
        })
        .catch(() => {
          void setSessionIdParam(null);
        });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync: when the copilot is visible and has an active session, ensure
  // the param is in the URL. When it's hidden or reset, clear it.
  // Skip while initializing to avoid a race where the param is cleared
  // before session.initialize() resolves.
  useEffect(() => {
    if (status === 'initializing') {
      return;
    }

    const currentId = session.id;

    if (isCopilotVisible && status === 'ready' && currentId != null && sessionIdParam !== currentId) {
      void setSessionIdParam(currentId);
    }

    if ((!isCopilotVisible || (status === 'idle' && currentId == null)) && sessionIdParam != null) {
      void setSessionIdParam(null);
    }
  }, [isCopilotVisible, status, session.id, sessionIdParam, setSessionIdParam]);
}
